/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { pgTable, text, uuid, timestamp } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { organizations } from "./organizations";

/**
 * Products table - stores software products and services in the portfolio
 * Uses UUID as the primary identifier for multi-organization support.
 * Each product belongs to exactly one organization.
 */
export const products = pgTable("products", {
  id: uuid("id").primaryKey().defaultRandom(), // UUID primary key - the authoritative identifier
  organizationId: uuid("organization_id").notNull().references(() => organizations.id, { onDelete: 'cascade' }), // Organization foreign key
  productId: text("product_id"), // Display-only identifier (format: PROD-XXXX). Not unique, not used for lookups.
  title: text("title").notNull(),
  description: text("description").notNull(),
  owner: text("owner").notNull(), // Person or team name within the organization
  links: text("links").array(), // Related URLs, documents, or resources
  capabilities: text("capabilities").array(), // Broad feature categories
  users: text("users").array(), // User roles (e.g., 'User', 'Admin', 'Product Manager')
  repositoryUrl: text("repository_url"), // Repository URL (remote or local/). All products should have one; enforced at API level.
  repoPath: text("repo_path"), // Relative path within the repo where the product lives
  architectureDiagram: text("architecture_diagram"), // Mermaid diagram showing system architecture
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Insert schema - allow optional id and productId for data loading, omit timestamps
export const insertProductSchema = createInsertSchema(products).omit({
  createdAt: true,
  updatedAt: true,
}).extend({
  id: z.string().uuid().optional(), // Allow optional id for data loading with predefined UUIDs
  productId: z.string().optional().nullable(), // Display-only field, optional
});

// Update schema - make all fields optional except id
export const updateProductSchema = insertProductSchema.partial().extend({
  id: z.string().uuid(),
});

// Type exports
export type Product = typeof products.$inferSelect;
export type InsertProduct = z.infer<typeof insertProductSchema>;
export type UpdateProduct = z.infer<typeof updateProductSchema>;

